import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { ReactComponent as FilterIcon } from 'assets/svg/FilterIcon.svg'
import { SortFieldsRejected, StatusPayment } from 'typescript/enums/transactions'
import { useMutation, useQuery } from 'react-query'
import { getRejected, getSummaryRejected, resolveDealPayment } from 'services/transactions'
import { usePaginationList } from 'hooks/usePaginationList'
import { IRejectedListItem, IRejectedSummary } from 'typescript/interfaces/transactions'
import Grid from '@material-ui/core/Grid'
import Skeleton from '@material-ui/lab/Skeleton'
import TotalBlockDeal from 'components/FundingQues/TotalBlockDeal'
import { useAdditionalMaterialStyle } from 'containers/MaterialUiContainer/additionalStyles'
import { TableRejectedQueues, useRejectedQueuesTable } from 'hooks/useRejectedQueuesTable'
import ModalComponent from 'UI/Modal'
import { IconButton, SvgIcon } from '@material-ui/core'
import DealPaymentForm from 'components/DealPaymentForm'
import { ReactComponent as CancelIcon } from 'assets/svg/CloseIcon.svg'
import cn from 'classnames'
import { getClientBankAccounts } from 'services/clients'
import { useSnackbar } from 'notistack'
import { SOMETHING_WENT_WRONG } from 'constants/errors'
import { IDealPaymentFormValues, IRejectedFilters } from 'typescript/interfaces/deals'
import { UserContext } from 'contexts/userContext'
import SelectGroup from 'UI/Select/SelectGroup'
import RepAutoComplete from 'components/RepAutoComplete'
import ISOAutoComplete from 'components/ISOAutoComplete'
import { useLatestDealsStyle } from 'containers/Deal/LatestDeals/style'
import CustomIconButton from 'UI/Button/IconButton'
import { useFormik } from 'formik'
import SearchField from 'UI/SearchField'
import RejectedStatusAutoComplete from 'components/Dashboard/RejectedStatusAutoComplete'
import { endOfMonth, startOfMonth } from 'date-fns'
import DatePicker from 'UI/DatePicker/DatePicker'

const RejectedQueueContainer: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [resolvePayment, setResolvePayment] = useState<IRejectedListItem>()
  const classes = useAdditionalMaterialStyle()
  const s = useLatestDealsStyle()
  const {
    state: { roles },
  } = useContext(UserContext)

  const { pagination, setPagination, handleSort, handleFetchMore, handleSearch } = usePaginationList<IRejectedListItem>({
    initState: {
      data: [],
      search: '',
      order: SortFieldsRejected.dateOfRejection,
      sortOrder: 'DESC',
      page: 0,
      hasMore: true,
    },
  })

  const [dataSummary, setDataSummary] = useState<IRejectedSummary>({
    rejectedCount: 0,
    resolvedCount: 0,
    totalRejected: 0,
    totalResolved: 0,
  })

  const { data: dataBankAccounts, isLoading: isLoadingBankAccounts } = useQuery(
    ['bankAccounts', resolvePayment?.clientId],
    () => getClientBankAccounts({ clientId: resolvePayment?.clientId || 0 }),
    { enabled: resolvePayment?.clientId },
  )

  const { columns } = useRejectedQueuesTable({ resolve: (data) => setResolvePayment(data) })

  const [getRejectedMut, { isLoading }] = useMutation(getRejected, {
    onSuccess: (data) => {
      setPagination((prevState) => ({
        ...prevState,
        data: [...prevState.data, ...data.data],
        hasMore: data.data.length === 20,
      }))
    },
  })

  const [getRejectedSummaryMut, { isLoading: isLoadingSummary }] = useMutation(getSummaryRejected, {
    onSuccess: (res) => {
      setDataSummary(res.data)
    },
  })

  const [resolveDealPaymentMut, { isLoading: isPaymentLoading }] = useMutation(resolveDealPayment, {
    onSuccess: () => {
      enqueueSnackbar('Payment successfully resolved')
      setResolvePayment(undefined)

      getRejectedSummaryMut({
        Query: encodeURIComponent(pagination.search),
        RepresentativeId: pagination.repId,
        IsoId: pagination.isoId,
        FromDate: pagination.fromDate ? new Date(pagination.fromDate).toISOString() : undefined,
        ToDate: pagination.toDate ? new Date(pagination.toDate).toISOString() : undefined,
        Status: pagination.status,
      })

      setPagination((prev) => ({
        ...prev,
        data: [],
        page: Number.EPSILON,
      }))
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const handleResolveDealPaymentMut = (formValues: IDealPaymentFormValues) => {
    if (resolvePayment) {
      resolveDealPaymentMut({ dealId: resolvePayment.dealId, rejectedId: resolvePayment.paymentId, ...formValues })
    }
  }

  useEffect(() => {
    getRejectedMut({
      PageSize: 20,
      PageIndex: Math.floor(pagination.page),
      SortField: pagination.order,
      SortOrder: pagination.sortOrder,
      Query: encodeURIComponent(pagination.search),
      RepresentativeId: pagination.repId,
      IsoId: pagination.isoId,
      FromDate: pagination.fromDate ? new Date(pagination.fromDate).toISOString() : undefined,
      ToDate: pagination.toDate ? new Date(pagination.toDate).toISOString() : undefined,
      Status: pagination.status
    })
  }, [
    pagination.page,
    pagination.order,
    pagination.sortOrder,
    pagination.search,
    pagination.repId,
    pagination.isoId,
    pagination.fromDate,
    pagination.toDate,
    pagination.status,
    pagination.search,
  ])

  useEffect(() => {
    getRejectedSummaryMut({
      Query: encodeURIComponent(pagination.search),
      RepresentativeId: pagination.repId,
      IsoId: pagination.isoId,
      FromDate: pagination.fromDate ? new Date(pagination.fromDate).toISOString() : undefined,
      ToDate: pagination.toDate ? new Date(pagination.toDate).toISOString() : undefined,
      Status: pagination.status
    })
  }, [
    pagination.search,
    pagination.repId,
    pagination.isoId,
    pagination.fromDate,
    pagination.toDate,
    pagination.status,
  ])

  const handleFiltersSubmit = (filtersToSet: IRejectedFilters) => {
    if (
      filtersToSet.repId === pagination.repId &&
      filtersToSet.isoId === pagination.isoId &&
      filtersToSet.fromDate === pagination.fromDate &&
      filtersToSet.toDate === pagination.toDate &&
      filtersToSet.status?.toString() === pagination.status?.toString()
    )
      return
    setPagination((prevState) => ({
      ...prevState,
      ...filtersToSet,
      page: 0,
      hasMore: true,
      data: [],
    }))
  }

  const { values: filters, setValues, handleSubmit, errors, touched, handleChange, setFieldValue } = useFormik<IRejectedFilters>({
    initialValues: {
      repId: undefined,
      isoId: undefined,
      fromDate: null,
      toDate: null,
      status: undefined,
    },
    onSubmit: handleFiltersSubmit,
  })

  const handleRepChange = useCallback((event: React.ChangeEvent<{}>, value: any) => {
    setValues((prevState) => ({
      ...prevState,
      repId: value?.map((v: any) => v.id),
    }))
  }, [])

  const handleIsoChange = useCallback((event: React.ChangeEvent<{}>, value: any) => {
    setValues((prevState) => ({
      ...prevState,
      isoId: value?.map((v: any) => v.id),
    }))
  }, [])

  const handleChangeStatuses = useCallback((e: React.ChangeEvent<{}>, values) => {
    setValues((prevState) => ({
      ...prevState,
      status: values ? values[0] : undefined,
    }))
  }, [])

  return (
    <>
      <Paper className={classes.wrapper} elevation={0}>
        <Box p="3rem 3rem 1rem 3rem">
          <Grid container spacing={5} justify="space-between" alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h3" color="textSecondary">
                Rejected Payments
                </Typography>
            </Grid>

            <Grid item xs={4}>
              <Box minWidth={135} ml={3}>
                <SearchField handleSearch={handleSearch} name="search" onChange={handleChange} label="Search the deal" fullWidth />
              </Box>
            </Grid>
          </Grid>
          

          <Box mt={8}>
            <Box p="1.5rem 1rem" className={s.roundBorder} marginTop="1.6rem">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box mr="1.5rem">
                  <Typography color="textSecondary" noWrap>
                    Filter:
                  </Typography>
                </Box>
                <Box mb="-0.25rem" flex={2} ml={2} maxWidth={810} minWidth={250}>
                  <SelectGroup>
                    {!roles.isRep && !roles.isISO && (
                      <RepAutoComplete multiple onChange={handleRepChange} className={s.autocomplete} repId={filters.repId} />
                    )}
                    {!roles.isISO && <ISOAutoComplete multiple onChange={handleIsoChange} className={s.autocomplete} isoId={filters.isoId} />}
                    <RejectedStatusAutoComplete
                        className={s.paidStatusAutoComplete}
                        statuses={filters.status ? [filters.status] : []}
                      onChange={handleChangeStatuses}
                      multiple={false}
                      />
                  </SelectGroup>
                </Box>

                <Box ml="1rem" mr="1rem">
                  <Typography color="textSecondary" noWrap>
                    Date of rejection:
                  </Typography>
                </Box>
                <Box>
                  <DatePicker
                    format="MMMM yyyy"
                    views={['year', 'month']}
                    value={filters.fromDate}
                    name="from"
                    error={Boolean(touched.fromDate && errors.fromDate)}
                    helperText={touched.fromDate && errors.fromDate}
                    onChange={(date) => {
                      setFieldValue('fromDate', date ? startOfMonth(new Date(date.toISOString())) : null)
                    }}
                    label="From"
                  />
                </Box>
                <Box ml="1rem">
                  <DatePicker
                    format="MMMM yyyy"
                    views={['year', 'month']}
                    value={filters.toDate}
                    name="ToDate"
                    error={Boolean(touched.toDate && errors.toDate)}
                    helperText={touched.toDate && errors.toDate}
                    onChange={(date) => {
                      setFieldValue('toDate', date ? endOfMonth(new Date(date.toISOString())) : null)
                    }}
                    label="To"
                  />
                </Box>
                <Box minWidth={36} width={138} ml={3}>
                  <CustomIconButton onClick={() => handleSubmit()} size="small" color="secondary" width="100%" height={36}>
                    <SvgIcon fontSize="inherit" viewBox="0 0 16 13" color="secondary" component={FilterIcon} />
                  </CustomIconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box marginLeft="3rem" paddingBottom="2rem">
          <Grid container spacing={5}>
            <Grid item xs={4}>
              {isLoadingSummary ? (
                <Skeleton height={150} />
              ) : (
                <TotalBlockDeal
                  label="Unresolved"
                  count={(dataSummary?.rejectedCount ?? 0)}
                  total={(dataSummary?.totalRejected ?? 0)}
                />
              )}
            </Grid>
            <Grid item xs={4}>
              {isLoadingSummary ? (
                <Skeleton height={150} />
              ) : (
                <TotalBlockDeal label="Resolved" count={dataSummary?.resolvedCount ?? 0} total={dataSummary?.totalResolved ?? 0} />
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
    
      <Box className={classes.wrapper} mt="1rem">
        <Paper elevation={0}>
          <TableRejectedQueues
            loading={isLoading}
            hasMore={pagination.hasMore}
            handleSortChange={handleSort}
            handleGetMore={handleFetchMore}
            columns={columns}
            data={pagination.data}
          />
        </Paper>
      </Box>
    
      <ModalComponent open={!!resolvePayment}>
        <Box maxWidth={500} maxHeight={800}>
          <Box display="flex" justifyContent="flex-end" mt="-1rem" mr="-1rem">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => setResolvePayment(undefined)}
              classes={{ root: cn(classes.closeTrsModalButton) }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
          <Typography variant="h2" color="textSecondary">
            Resolve Payment #{resolvePayment?.paymentId} - Deal #{resolvePayment?.dealId}
          </Typography>
          <Box mt="2rem">
            <DealPaymentForm
              resolveMode
              initialValues={{
                scheduledFor: resolvePayment?.scheduledFor,
                amount: resolvePayment?.remainingAmount,
                status: StatusPayment.successful,
                category: '',
                paymentDate: '',
                comments: `Resolved rejected payment #${resolvePayment?.paymentId}`,
              }}
              bankAccounts={dataBankAccounts?.data || []}
              cancel={() => setResolvePayment(undefined)}
              submit={(formValues) => handleResolveDealPaymentMut(formValues)}
              loading={isLoadingBankAccounts || isPaymentLoading}
            />
          </Box>
        </Box>
      </ModalComponent>
    </>
  )
}

export default RejectedQueueContainer
